import { DetailBreadcrumbsComponent } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { CanPurgePipe } from '@modules/common/pipes/can-purge.pipe';
import { AlertModule } from '@modules/alert/alert.module';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuDividerComponent } from '@frontendlib/components/features/popover/components/context-menu-divider/context-menu-divider.component';
import { ContextMenuHeadingComponent } from '@frontendlib/components/features/popover/components/context-menu-heading/context-menu-heading.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { SearchLocationBadgeComponent } from '@frontendlib/components/components/search-location-badge/search-location-badge.component';
import { CommentsModule } from '@modules/comments/comments.module';
import { ListItemSeparatorComponent } from '@modules/common/components/list/list-items-separator/list-items-separator.component';
import { ListSelectionBarComponent } from '@modules/common/components/list/list-selection-bar/list-selection-bar.component';
import { ListLoadErrorComponent } from '@modules/common/components/list/list-load-error/list-load-error.component';
import { Pagination2Component } from '@modules/common/components/pagination/pagination2.component';
import { SidebarItemComponent } from '@modules/common/components/sidebar-item/sidebar-item.component';
import { ItemDescriptionFieldComponent } from '@modules/common/components/item-description/item-description-field.component';
import { StitchTimestampsComponent } from '@modules/common/components/stitch-timestamps/stitch-timestamps.component';
import { ConnectionsModule } from '@modules/connections/connections.module';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { LinkedInfoListComponent } from '@modules/elements/components/link-info/linked-info-list/linked-info-list.component';
import { EntityHubSearchComponent } from '@modules/elements/components/entity-hub/entity-hub-search/entity-hub-search.component';
import { StitchHubComponent } from '@modules/elements/components/stitch-hub/stitch-hub.component';
import { QuickFormComponent } from '@modules/elements/components/quick-forms/quick-form/quick-form.component';
import { SheetFolderQuickFormComponent } from '@modules/elements/components/quick-forms/sheet-folder-quick-form/sheet-folder-quick-form.component';
import { SheetQuickFormComponent } from '@modules/elements/components/quick-forms/sheet-quick-form/sheet-quick-form.component';
import { SheetContextMenuDirective } from '@modules/elements/directives/sheet-context-menu.directive';
import { SheetFolderContextMenuDirective } from '@modules/elements/directives/sheet-folder-context-menu.directive';
import { QuickActionComponent } from '@modules/form-controls/components/quick-action/quick-action.component';
import { FormControlsModule } from '@modules/form-controls/form-controls.module';
import { KnotsModule } from '@modules/knots/knots.module';
import { ScrollModule } from '@modules/scroll/scroll.module';
import { TagsModule } from '@modules/tags/tags.module';
import { ToArrayPipe } from '@modules/utils/pipes/array/to-array.pipe';
import { UtilsModule } from '@modules/utils/utils.module';
import { CommonModule as AppCommonModule } from '../common/common.module';
import { ElementsModule } from '../elements/elements.module';
import { SheetFolderFormComponent } from './components/sheet-folder-form/sheet-folder-form.component';
import { SheetFolderSectionsComponent } from './components/sheet-folder-sections/sheet-folder-sections.component';
import { SheetFoldersListContextMenuComponent } from './components/sheet-folders-list-context-menu/sheet-folders-list-context-menu.component';
import { SheetFoldersListComponent } from './components/sheet-folders-list/sheet-folders-list.component';
import { SheetFormComponent } from './components/sheet-form/sheet-form.component';
import { SheetsListContainerComponent } from './components/sheets-list-container/sheets-list-container.component';
import { SheetsListContextMenuComponent } from './components/sheets-list-context-menu/sheets-list-context-menu.component';
import { SheetsListComponent } from './components/sheets-list/sheets-list.component';
import { SheetsMainViewComponent } from './components/sheets-main-view/sheets-main-view.component';
import { SideBarSheetFoldersTreeComponent } from './components/side-bar-sheet-folders-tree/side-bar-sheet-folders-tree.component';
import { SideBarSheetFoldersComponent } from './components/side-bar-sheet-folders/side-bar-sheet-folders.component';
import { SheetsSideBarComponent } from './components/side-bar/side-bar.component';
import { SidebarContextMenuComponent } from './components/sidebar-context-menu/sidebar-context-menu.component';
import { SheetFoldersListContextMenuDirective } from './directives/sheet-folders-list-context-menu.directive';
import { SheetsListContextMenuDirective } from './directives/sheets-list-context-menu.directive';
import { SheetsPermissionDirective } from './directives/sheets-permission.directive';
import { SidebarContextMenuDirective } from './directives/sidebar-context-menu.directive';
import { SheetsPermissionPipe } from './pipes/sheets-permission.pipe';
import { TitleBySheetsFilterPipe } from './pipes/title-by-sheets-filter.pipe';

// The workbook surface. Kept standalone and lazily referenced so the grid engine
// stays out of the Sheets section's initial chunk; see D-PREVIEW.
import { SheetsEditorComponent } from './components/sheets-editor/sheets-editor.component';
import { WorkbookPreviewComponent } from './workbook-preview.component';

@NgModule({
  imports: [
    DetailBreadcrumbsComponent,
    AlertModule,
    CanPurgePipe,
    AppCommonModule,
    CommentsModule,
    CommonModule,
    ConnectionsModule,
    DragNDropModule,
    ElementsModule,
    FormControlsModule,
    FormsModule,
    KnotsModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    ScrollModule,
    ScrollingModule,
    TagsModule,
    UtilsModule,
    Button2Component,
    ContextMenuDividerComponent,
    ContextMenuHeadingComponent,
    ContextMenuItemComponent,
    ContextMenuStateIndicatorComponent,
    IconComponent,
    ItemDescriptionFieldComponent,
    LinkedInfoListComponent,
    EntityHubSearchComponent,
    StitchHubComponent,
    ListItemSeparatorComponent,
    ListSelectionBarComponent,
    ListLoadErrorComponent,
    LoadingIndicatorComponent,
    NoContentStubComponent,
    Pagination2Component,
    PopoverDirective,
    QuickActionComponent,
    QuickFormComponent,
    SearchLocationBadgeComponent,
    SheetContextMenuDirective,
    SheetFolderContextMenuDirective,
    SheetFolderQuickFormComponent,
    SheetFormComponent,
    SheetQuickFormComponent,
    SheetsEditorComponent,
    SheetsPermissionDirective,
    SheetsPermissionPipe,
    SidebarItemComponent,
    StitchTimestampsComponent,
    ToArrayPipe,
    WorkbookPreviewComponent,
  ],
  declarations: [
    // Components
    SheetFolderFormComponent,
    SheetFolderSectionsComponent,
    SheetFoldersListComponent,
    SheetFoldersListContextMenuComponent,
    SheetsListComponent,
    SheetsListContainerComponent,
    SheetsListContextMenuComponent,
    SheetsMainViewComponent,
    SheetsSideBarComponent,
    SideBarSheetFoldersComponent,
    SideBarSheetFoldersTreeComponent,
    SidebarContextMenuComponent,
    // Directives
    SheetFoldersListContextMenuDirective,
    SheetsListContextMenuDirective,
    SidebarContextMenuDirective,
    // Pipes
    TitleBySheetsFilterPipe,
  ],
  exports: [
    // Components
    SheetFolderFormComponent,
    SheetFoldersListComponent,
    SheetFormComponent,
    SheetsListComponent,
    SheetsListContainerComponent,
    SheetsMainViewComponent,
    SheetsSideBarComponent,
    // Directives
    SheetFoldersListContextMenuDirective,
    SheetsListContextMenuDirective,
    // Pipes
    TitleBySheetsFilterPipe,
  ],
  providers: [],
})
export class SheetsModule {}
